import { NgModule } from '@angular/core';
import { Router, RouterModule, Routes } from '@angular/router';
import { AccountManagementComponent } from './account-management/account-management.component';
import { InvitationsComponent } from './auth/invitations/invitations.component';
import { LogInSignUpComponent } from './auth/log-in-sign-up/log-in-sign-up.component';
import { ResetPasswordComponent } from './auth/reset-password/reset-password.component';
import { AntiAuthGuard } from './guards/anti-auth.guard';
import { AuthGuard } from './guards/auth.guard';
import { DashboardGuard } from './guards/dashboard.guard';
import { OrgsGuard } from './guards/orgs.guard';
import { LedgerImportComponent } from './ledger/ledger-import/ledger-import.component';
import { LedgerResolverService } from './ledger/ledger-resolver.service';
import { LedgerComponent } from './ledger/ledger.component';
import { LedgersListComponent } from './ledger/ledgers-list/ledgers-list.component';
import { MainPageComponent } from './main-page/main-page.component';
import { TeamComponent } from './team/team.component';
import { SettingsComponent } from './settings/settings.component';
import { IntegrationsComponent } from './integrations/integrations.component';
import { ActiveOrgGuard } from './guards/active-org.guard';
import { OauthComponent } from './auth/oauth/oauth.component';
import { ExtensionComponent } from './auth/extension/extension.component';
import { OAuthConsentComponent } from './auth/oauth-consent/oauth-consent.component';
import { LedgerContactsV2Component } from './ledger/ledger-contacts-v2/ledger-contacts-v2/ledger-contacts-v2.component';
import { LedgerOverviewV2Component } from './ledger/ledger-overview-v2/ledger-overview-v2.component';
import { PeopleDetailsComponent } from './people/details/details.component';
import { DWPeopleComponent } from './people/people.component';
import { FiltersComponent } from './people/filters/filters.component';
import { PeopleFindComponent } from './people/find/find.component';
import { ConfiguredIntegrationsComponent } from './integrations/configured-integrations/configured-integrations.component';
import { AvailableIntegrationsComponent } from './integrations/available-integrations/available-integrations.component';
import { IntegrationDialogComponent } from './integrations/available-integrations/integration-dialog/integration-dialog.component';
import { PeopleChatComponent } from './shared/components/chat/chat.component';
import { DownloadsComponent } from './downloads/downloads.component';
import { DownloadsListComponent } from './downloads/downloads-list/downloads-list.component';
import { HostnameService } from './hostname-router-service';
import { Log } from './logger.service';
import { DarkModeService } from './shared/dark-mode/dark-mode.service';
import { WorkforceInsightsComponent } from './workforce/insights/insights.component';
import { ChatWithInsightsComponent } from './workforce/chat-with-insights/chat-with-insights.component';
import { landingRedirectGuard } from './guards/landing-redirect.guard';
import { integrationsLandingGuard } from './guards/integrations-landing.guard';
import { AccessLinksComponent } from './access-links/access-links.component';
import { OrgResolverService } from './resolvers/org-resolver.service';
import { SettingsBillingComponent } from './settings/billing/billing.component';
import { FindCompaniesComponent } from './people/find-companies/find-companies.component';
import { WorkforceAppComponent } from './workforce/app.wf.component';
import { GotlivedataAppComponent } from './gotlivedata/app.gld.component';
import { LedgerTrackedFieldsComponent } from './ledger/ledger-tracked-fields/ledger-tracked-fields.component';
import { WorkforcePeopleComponent } from './workforce/people/people.component';
import { WorkforceTeamComponent } from './workforce/team/team.component';
import { WorkforceApiKeysComponent } from './workforce/api-keys/api-keys.component';

const authRelatedRoutes: any = [
  {
    path: 'login',
    component: LogInSignUpComponent,
    data: { isLogin: true },
    canActivate: [AntiAuthGuard],
  },
  {
    path: 'signup',
    component: LogInSignUpComponent,
    data: { isLogin: false },
    canActivate: [AntiAuthGuard],
  },
  {
    path: 'login/extension',
    component: ExtensionComponent,
    canActivate: [],
  },
  {
    path: 'reset-password',
    component: ResetPasswordComponent,
    canActivate: [AntiAuthGuard],
  },
  {
    path: 'authorize',
    component: OAuthConsentComponent,
    canActivate: [AuthGuard],
  },
];

// Org-scoped features shared by both experiences, mounted under /:orgId.
const orgFeatureRoutes: any = [
  {
    path: 'ledgers',
    component: LedgersListComponent,
  },
  {
    path: 'ledgers/:ledgerId',
    component: LedgerComponent,
    resolve: {
      userdata: LedgerResolverService,
    },
    children: [
      { path: '', redirectTo: 'overview-v2', pathMatch: 'full' },
      {
        path: 'overview',
        redirectTo: 'overview-v2',
        pathMatch: 'full',
      },
      {
        path: 'overview-v2',
        component: LedgerOverviewV2Component,
      },
      {
        path: 'details',
        redirectTo: 'details-v2',
        pathMatch: 'full',
      },
      {
        path: 'details-v2',
        component: LedgerContactsV2Component,
      },
      {
        path: 'imports',
        component: LedgerImportComponent,
      },
      {
        path: 'tracked-fields',
        component: LedgerTrackedFieldsComponent,
      },
    ],
  },
  {
    path: 'downloads',
    component: DownloadsComponent,
    children: [
      { path: '', redirectTo: 'downloads-list', pathMatch: 'full' },
      { path: 'downloads-list', component: DownloadsListComponent },
    ],
  },
  {
    path: 'integrations',
    component: IntegrationsComponent,
    children: [
      { path: '', pathMatch: 'full', canActivate: [integrationsLandingGuard], children: [] },
      {
        path: 'available-integrations',
        component: AvailableIntegrationsComponent,
        children: [
          {
            path: 'integration-dialog',
            component: IntegrationDialogComponent,
          },
        ],
      },
      { path: 'configured-integrations', component: ConfiguredIntegrationsComponent },
    ],
  },
];

// Internal admin area, shared by both experiences (flat routes, DashboardGuard).
const adminSectionRoute: any = {
  path: 'admin',
  canActivate: [DashboardGuard],
  loadComponent: () => import('./admin/admin-shell.component').then(m => m.AdminShellComponent),
  children: [
    {
      path: 'identity',
      loadComponent: () => import('./admin/identity/identity.component').then(m => m.IdentityComponent)
    },
    {
      path: 'ledger',
      loadComponent: () => import('./admin/ledger/ledger.component').then(m => m.AdminLedgerComponent)
    },
    {
      path: 'identity/orgs/:orgId',
      loadComponent: () => import('./admin/identity/org-details-shell/org-details-shell.component').then(m => m.OrgDetailsShellComponent),
      children: [
        { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
        {
          path: 'dashboard',
          loadComponent: () => import('./org-dashboard/org-dashboard.component').then(m => m.OrgDashboardComponent),
        },
        {
          path: 'settings',
          loadComponent: () => import('./admin/identity/org-details/identity-org-details.component').then(m => m.IdentityOrgDetailsComponent),
        },
        {
          path: 'credits',
          loadComponent: () => import('./admin/identity/org-credits/org-credits.component').then(m => m.OrgCreditsComponent),
        },
      ],
    },
    {
      path: 'firehose',
      loadComponent: () => import('./admin/firehose/firehose.component').then(m => m.FirehoseComponent)
    },
    {
      path: 'grow',
      loadComponent: () => import('./grow/grow.component').then(m => m.GrowComponent),
    },
    {
      path: 'techno-target',
      loadComponent: () => import('./techno-target/techno-target.component').then(m => m.TechnoTargetComponent),
    },
    {
      path: 'data-deliveries',
      loadComponent: () => import('./admin/data-deliveries/data-deliveries.component').then(m => m.DataDeliveriesComponent),
      children: [
        { path: '', redirectTo: 'report-deliveries', pathMatch: 'full' },
        {
          path: 'report-deliveries',
          loadComponent: () => import('./admin/data-deliveries/report-deliveries/report-deliveries.component').then(m => m.ReportDeliveriesComponent),
        },
        {
          path: 'destinations',
          loadComponent: () => import('./admin/data-deliveries/destinations/destinations.component').then(m => m.DestinationsComponent),
        },
        {
          path: 'destination-types',
          loadComponent: () => import('./admin/data-deliveries/destination-types/destination-types.component').then(m => m.DestinationTypesComponent),
        },
        {
          path: 'report-definitions',
          loadComponent: () => import('./admin/data-deliveries/report-definitions/report-definitions.component').then(m => m.ReportDefinitionsComponent),
        },
        {
          path: 'scheduled-reports',
          loadComponent: () => import('./admin/data-deliveries/scheduled-reports/scheduled-reports.component').then(m => m.ScheduledReportsComponent),
        },
        {
          path: 'report-sources',
          loadComponent: () => import('./admin/data-deliveries/report-sources/report-sources.component').then(m => m.ReportSourcesComponent),
        },
      ],
    },
    {
      path: 'billing',
      loadComponent: () => import('./admin/billing/billing.component').then(m => m.AdminBillingComponent),
      children: [
        { path: '', redirectTo: 'subscriptions', pathMatch: 'full' },
        {
          path: 'subscriptions',
          loadComponent: () => import('./admin/billing/subscriptions/subscriptions.component').then(m => m.AdminBillingSubscriptionsComponent),
        },
        {
          path: 'products',
          loadComponent: () => import('./admin/billing/products/products.component').then(m => m.AdminBillingProductsComponent),
        },
      ],
    },
    {
      path: 'staging-firehose',
      loadComponent: () => import('./admin/staging-firehose/staging-firehose.component').then(m => m.StagingFirehoseComponent),
    },
    {
      path: 'moneyball',
      loadComponent: () => import('./admin/moneyball/moneyball.component').then(m => m.AdminMoneyballComponent),
    },
    {
      path: 'clean',
      loadComponent: () => import('./admin/clean/clean.component').then(m => m.CleanComponent),
    },
    {
      path: 'streams',
      loadComponent: () => import('./admin/onboarding/onboarding.component').then(m => m.AdminStreamsComponent),
      children: [
        { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
        {
          path: 'dashboard',
          loadComponent: () => import('./admin/onboarding/dashboard/dashboard.component').then(m => m.DashboardComponent)
        },
        {
          path: 'trials',
          loadComponent: () => import('./admin/onboarding/streams-trial/streams-trial.component').then(m => m.StreamsTrialComponent)
        },
        {
          path: 'companies',
          loadComponent: () => import('./admin/onboarding/streams-companies/streams-companies.component').then(m => m.StreamsCompaniesComponent)
        },
        {
          path: 'access-links',
          loadComponent: () => import('./admin/onboarding/access-links/access-links.component').then(m => m.AdminOnboardingAccessLinksComponent),
          data: { scope: 'global' },
        },
      ],
    },
    {
      path: 'workforce',
      loadComponent: () => import('./admin/workforce/workforce.component').then(m => m.AdminWorkforceComponent),
      children: [
        { path: '', redirectTo: 'quickbuilds', pathMatch: 'full' },
        {
          path: 'company-lists',
          loadComponent: () => import('./admin/workforce/company-lists/company-lists.component').then(m => m.AdminWorkforceCompanyListsComponent),
        },
        {
          path: 'quickbuilds',
          loadComponent: () => import('./admin/workforce/quickbuilds/quickbuilds.component').then(m => m.AdminWorkforceQuickbuildsComponent),
        },
        {
          path: 'role-definitions',
          loadComponent: () => import('./admin/workforce/role-definitions/role-definitions.component').then(m => m.AdminWorkforceRoleDefinitionsComponent),
        },
      ],
    },
    {
      path: 'providers',
      loadComponent: () => import('./admin/providers/providers.component').then(m => m.ProvidersComponent)
    },
  ],
};

// Data warehouse tools, shared by both experiences (flat routes, DashboardGuard).
const dataWarehouseSectionRoute: any = {
  path: 'datawarehouse',
  canActivate: [DashboardGuard],
  loadComponent: () => import('./admin/data-warehouse/data-warehouse.component').then(m => m.DataWarehouseComponent),
  children: [
    { path: '', redirectTo: 'stats', pathMatch: 'full' },
    {
      path: 'search',
      loadComponent: () => import('./admin/data-warehouse/custom-search/custom-search.component').then(m => m.CustomSearchComponent),
    },
    {
      path: 'stats',
      loadComponent: () => import('./admin/data-warehouse/stats/stats.component').then(m => m.StatsComponent),
    },
    {
      path: 'details',
      loadComponent: () => import('./admin/data-warehouse/details/details.component').then(m => m.DetailsComponent),
    },
    {
      path: 'inspect',
      loadComponent: () => import('./admin/data-warehouse/inspect/inspect.component').then(m => m.InspectComponent),
    },
    {
      path: 'query',
      loadComponent: () => import('./admin/data-warehouse/os-query/os-query.component').then(m => m.OsQueryComponent),
    },
    {
      path: 'aliases',
      loadComponent: () => import('./admin/data-warehouse/aliases/aliases.component').then(m => m.AliasesComponent),
    },
    {
      path: 'companies',
      loadComponent: () => import('./admin/data-warehouse/companies/companies.component').then(m => m.CompaniesComponent),
    },
    {
      path: 'resolver',
      loadComponent: () => import('./admin/data-warehouse/resolver/resolver.component').then(m => m.ResolverComponent),
    },
  ],
};

const MainRoutes: any = [
  {
    path: 'main',
    component: MainPageComponent,
    canActivate: [AuthGuard],
  },
  ...authRelatedRoutes,
  {
    path: 'go/:token',
    component: AccessLinksComponent,
  },
  {
    path: '',
    component: GotlivedataAppComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'account',
        component: AccountManagementComponent,
      },
      {
        path: 'invitations',
        component: InvitationsComponent,
      },
      {
        path: 'invite',
        component: InvitationsComponent,
      },
      {
        path: 'edit-my-posts',
        loadComponent: () => import('./admin/onboarding/access-links/access-links.component').then(m => m.AdminOnboardingAccessLinksComponent),
      },
      {
        path: 'oauth',
        component: OauthComponent,
      },
      adminSectionRoute,
      dataWarehouseSectionRoute,
      {
        path: ':orgId',
        canActivate: [OrgsGuard, ActiveOrgGuard],
        resolve: { org: OrgResolverService },
        children: [
          ...orgFeatureRoutes,
          {
            path: 'team',
            component: TeamComponent,
          },
          {
            path: 'people',
            component: DWPeopleComponent,
            children: [
              { path: '', redirectTo: 'details', pathMatch: 'full' },
              {
                path: 'details',
                component: PeopleDetailsComponent,
              },
              {
                path: 'filters',
                component: FiltersComponent,
              },
              {
                path: 'find',
                component: PeopleFindComponent,
              },
              {
                path: 'find-companies',
                component: FindCompaniesComponent,
              },
              {
                path: 'chat',
                component: PeopleChatComponent,
              },
            ],
          },
          {
            path: 'settings',
            component: SettingsComponent,
          },
        ],
      },
      {
        path: '',
        redirectTo: '/main',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: '**',
    component: MainPageComponent,
    data: { pageNotFound: true },
  },
];

@NgModule({
  imports: [RouterModule.forRoot([])],
  exports: [RouterModule],
})
export class AppRoutingModule {
  routes: Routes = [];

  constructor(
    private router: Router,
    private darkModeService: DarkModeService
  ) {
    // The Workforce experience is the default on every hostname. The GotLiveData
    // experience is deprecated — reachable only via the Alt+Shift+W hotkey.
    this.configureRoutes('workforce');
  }

  public configureRoutes(appName: 'workforce' | 'gotlivedata') {
    HostnameService.setAppExperience(appName);
    if (appName === 'workforce') {
      this.darkModeService.setDarkMode(true);
      // Set the WorkforceComponent as the root component
      this.routes = [
        ...authRelatedRoutes,
        {
          path: 'go/:token',
          component: AccessLinksComponent,
          canActivate: [],
        },
        {
          path: '',
          component: WorkforceAppComponent,
          canActivate: [AuthGuard],
          children: [
            {
              path: 'chat',
              component: ChatWithInsightsComponent,
            },
            {
              path: 'insights',
              component: WorkforceInsightsComponent,
            },
            {
              path: 'billing',
              component: SettingsBillingComponent,
            },
            {
              path: 'invite',
              component: InvitationsComponent,
            },
            {
              path: 'team',
              component: WorkforceTeamComponent,
            },
            {
              path: 'api-keys',
              component: WorkforceApiKeysComponent,
            },
            {
              path: 'people',
              children: [
                {
                  path: '',
                  redirectTo: 'explore',
                  pathMatch: 'full',
                },
                {
                  component: WorkforcePeopleComponent,
                  path: 'explore',
                  data: {
                    mode: 'explore',
                  },
                },
                {
                  component: WorkforcePeopleComponent,
                  path: 'search',
                  data: {
                    mode: 'search',
                  },
                },
                {
                  component: WorkforcePeopleComponent,
                  path: 'bulk',
                  data: {
                    mode: 'bulk',
                  },
                },
                {
                  component: WorkforcePeopleComponent,
                  path: 'find',
                  data: {
                    mode: 'find',
                  },
                },
                {
                  component: WorkforcePeopleComponent,
                  path: 'find-companies',
                  data: {
                    mode: 'find-companies',
                  },
                },
              ],
            },
            {
              path: 'account',
              component: AccountManagementComponent,
            },
            {
              // OAuth callback for third-party integration connects; must keep
              // working for redirect URIs registered as <host>/oauth
              path: 'oauth',
              component: OauthComponent,
            },
            {
              path: 'invitations',
              redirectTo: 'invite',
            },
            {
              // Legacy GLD landing URL (also still a fallback target in a few
              // services); hand it to the capability landing redirect.
              path: 'main',
              redirectTo: '',
              pathMatch: 'full',
            },
            adminSectionRoute,
            dataWarehouseSectionRoute,
            {
              // Org-scoped features ported from the GotLiveData experience
              // (ledgers, downloads, integrations).
              path: ':orgId',
              canActivate: [OrgsGuard, ActiveOrgGuard],
              resolve: { org: OrgResolverService },
              children: [
                ...orgFeatureRoutes,
                // Legacy GotLiveData URLs -> their Workforce-shell equivalents
                { path: 'team', redirectTo: '/team' },
                { path: 'settings', redirectTo: '/billing' },
                { path: 'people/details', redirectTo: '/people/explore' },
                { path: 'people/filters', redirectTo: '/people/search' },
                { path: 'people/find', redirectTo: '/people/find' },
                { path: 'people/find-companies', redirectTo: '/people/find-companies' },
                { path: 'people/chat', redirectTo: '/chat' },
                { path: 'people', redirectTo: '/people', pathMatch: 'full' },
              ],
            },
            { path: '', pathMatch: 'full', canActivate: [landingRedirectGuard], children: [] },
          ],
        },
        // Legacy org-scoped billing URL. Declared AFTER the shell route so it
        // can't shadow real routes like /admin/billing/* (":orgId" matched
        // "admin" when this sat first in the table).
        {
          path: ':orgId/billing',
          redirectTo: 'billing',
        },
        // Other workforce-specific routes
        { path: '**', canActivate: [landingRedirectGuard], children: [] },
      ];
    } else {
      // Default routes for gotlivedata.com
      this.routes = MainRoutes;
    }

    Log.d('Resetting routes', this.routes);
    this.router.resetConfig(this.routes);
  }
}
